import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Briefcase, CheckCircle, Clock, Pencil, X } from "lucide-react";
import { timeAgo } from "@/lib/time-ago";
import { useCategories } from "@/hooks/use-categories";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

interface Job {
  id: number;
  title: string;
  category: string;
  town: string;
  urgency: string;
  budgetRange: string;
  description: string;
  contactName: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  approved: boolean;
  createdAt: string;
}

const URGENCY_STYLES: Record<string, string> = {
  urgent: "bg-red-100 text-red-700",
  soon: "bg-amber-100 text-amber-700",
  flexible: "bg-slate-100 text-slate-600",
};

export default function AdminJobs() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved">("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Job | null>(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", category: "", town: "", urgency: "", budgetRange: "", contactName: "", phone: "" });
  const jobCategories = useCategories("job");

  const isMod = (user as { isModerator?: boolean } | null)?.isModerator === true;
  if (authLoading) return null;
  if (!user || (user.role !== "admin" && !isMod)) return <Redirect to="/" />;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/admin/content/jobs", { headers: authHeaders() });
      const data = await r.json() as { items: Job[] };
      setItems(data.items ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const markBusy = (id: number) => setBusy(p => new Set([...p, id]));
  const clearBusy = (id: number) => setBusy(p => { const s = new Set(p); s.delete(id); return s; });

  const handleDelete = async (item: Job) => {
    if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    markBusy(item.id);
    try {
      const r = await fetch(`/api/admin/content/jobs/${item.id}/reject`, { method: "PATCH", headers: authHeaders() });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Job deleted" });
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally {
      clearBusy(item.id);
    }
  };

  const handleApprove = async (item: Job) => {
    markBusy(item.id);
    try {
      const r = await fetch(`/api/admin/content/jobs/${item.id}/approve`, { method: "PATCH", headers: authHeaders() });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Job approved and tradespeople notified" });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, approved: true } : i));
    } catch {
      toast({ title: "Failed to approve", variant: "destructive" });
    } finally {
      clearBusy(item.id);
    }
  };

  const openEdit = (item: Job) => {
    setEditing(item);
    setEditForm({ title: item.title, description: item.description ?? "", category: item.category, town: item.town, urgency: item.urgency, budgetRange: item.budgetRange ?? "", contactName: item.contactName ?? "", phone: item.contactPhone ?? "" });
  };

  const saveEdit = async () => {
    if (!editing) return;
    markBusy(editing.id);
    try {
      const response = await fetch(`/api/admin/content/jobs/${editing.id}`, { method: "PUT", headers: authHeaders(), body: JSON.stringify(editForm) });
      const data = await response.json() as Job & { error?: string; categoryChanged?: boolean; notificationResult?: { matchingTrades?: number; tokensFound?: number } };
      if (!response.ok) throw new Error(data.error ?? "Failed to save");
      setItems((previous) => previous.map((item) => item.id === editing.id ? { ...item, ...data } : item));
      setEditing(null);
      toast({ title: data.categoryChanged ? "Category corrected and matching trades notified" : "Job listing updated", description: data.categoryChanged ? `${data.notificationResult?.matchingTrades ?? 0} matching trade profiles found; push and email delivery started.` : undefined });
    } catch (error) {
      toast({ title: error instanceof Error ? error.message : "Failed to save", variant: "destructive" });
    } finally { clearBusy(editing.id); }
  };

  const filtered = items
    .filter(i => filter === "all" ? true : filter === "pending" ? !i.approved : i.approved)
    .filter(i => !search || i.title.toLowerCase().includes(search.toLowerCase()) || i.town.toLowerCase().includes(search.toLowerCase()) || i.category.toLowerCase().includes(search.toLowerCase()));

  const pendingCount = items.filter(i => !i.approved).length;

  return (
    <AdminLayout current="/admin/content/jobs">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Job Listings</h1>
            <p className="text-sm text-slate-500 mt-0.5">{items.length} total · {pendingCount} pending approval</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(["all", "pending", "approved"] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all capitalize ${
                  filter === f ? "bg-primary text-white border-primary" : "bg-white border-slate-200 text-slate-600 hover:border-primary/40"
                }`}
              >{f === "pending" ? `Pending (${pendingCount})` : f}</button>
            ))}
          </div>
        </div>

        <input
          type="text"
          aria-label="Search by title, town or category"
          placeholder="Search by title, town or category…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary bg-white"
        />

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No jobs to show</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => {
              const urgencyStyle = URGENCY_STYLES[item.urgency] ?? "bg-slate-100 text-slate-600";
              const isBusy = busy.has(item.id);
              return (
                <div key={item.id} className={`bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex gap-4 ${!item.approved ? "border-l-4 border-l-amber-400" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-800 truncate">{item.title}</h3>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${urgencyStyle}`}>{item.urgency}</span>
                          {!item.approved && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                              <Clock size={10} /> Pending
                            </span>
                          )}
                          {item.approved && (
                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                              <CheckCircle size={10} /> Approved
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          📍 {item.town} · {item.category} · 💰 {item.budgetRange}
                          {item.contactName && ` · 👤 ${item.contactName}`}
                          {" · "}{timeAgo(item.createdAt)}
                        </p>
                        {(item.contactPhone || item.contactEmail) && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {item.contactPhone && `📞 ${item.contactPhone}`}
                            {item.contactPhone && item.contactEmail && " · "}
                            {item.contactEmail && `✉ ${item.contactEmail}`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button onClick={() => openEdit(item)} disabled={isBusy} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:bg-blue-900/20 transition-colors disabled:opacity-40" title="Edit"><Pencil size={15} /></button>
                        {!item.approved && (
                          <button
                            onClick={() => void handleApprove(item)}
                            disabled={isBusy}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
                            title="Approve"
                          >
                            <CheckCircle size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => void handleDelete(item)}
                          disabled={isBusy}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {editing && <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"><div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b"><div><h2 className="font-bold text-lg">Edit quote request</h2><p className="text-xs text-slate-500">Changing an approved category sends a fresh push and email to matching trades.</p></div><button onClick={() => setEditing(null)}><X size={20} /></button></div>
        <div className="p-5 grid sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2 text-xs font-semibold text-slate-600">Title<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={editForm.title} onChange={(event) => setEditForm({ ...editForm, title: event.target.value })} /></label>
          <label className="sm:col-span-2 text-xs font-semibold text-slate-600">Description<textarea className="mt-1 w-full border rounded-lg px-3 py-2 text-sm min-h-24" value={editForm.description} onChange={(event) => setEditForm({ ...editForm, description: event.target.value })} /></label>
          <label className="text-xs font-semibold text-slate-600">Category<select className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white" value={editForm.category} onChange={(event) => setEditForm({ ...editForm, category: event.target.value })}>{jobCategories.map((category) => <option key={category}>{category}</option>)}</select></label>
          <label className="text-xs font-semibold text-slate-600">Town<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={editForm.town} onChange={(event) => setEditForm({ ...editForm, town: event.target.value })} /></label>
          <label className="text-xs font-semibold text-slate-600">Urgency<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={editForm.urgency} onChange={(event) => setEditForm({ ...editForm, urgency: event.target.value })} /></label>
          <label className="text-xs font-semibold text-slate-600">Budget<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={editForm.budgetRange} onChange={(event) => setEditForm({ ...editForm, budgetRange: event.target.value })} /></label>
          <label className="text-xs font-semibold text-slate-600">Contact name<input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={editForm.contactName} onChange={(event) => setEditForm({ ...editForm, contactName: event.target.value })} /></label>
          <label className="text-xs font-semibold text-slate-600">Mobile number<input type="tel" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={editForm.phone} onChange={(event) => setEditForm({ ...editForm, phone: event.target.value })} /></label>
        </div>
        <div className="p-5 border-t flex justify-end gap-2"><button className="px-4 py-2 border rounded-lg text-sm" onClick={() => setEditing(null)}>Cancel</button><button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold" onClick={() => void saveEdit()}>Save changes</button></div>
      </div></div>}
    </AdminLayout>
  );
}
